import React, { useEffect, useMemo, useState } from "react";
import { X, Minus, Plus, Store, Bike, CreditCard, Copy, MessageCircle, Send, Bell, Tag, Check, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useStore } from "@/context/StoreContext";
import api, { mediaUrl } from "@/lib/api";
import { effectivePrice, unitDiscount, nextTierHint, computeTotals, money, unitPriceFor, itemDeliveryFee } from "@/lib/pricing";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const NOTIFY_KEY = "redeye_notify_dismissed";

export default function CartDrawer() {
  const { cartOpen, setCartOpen, cart, setQty, removeItem, clearCart, settings } = useStore();
  const [fulfillment, setFulfillment] = useState("");
  const [payMethod, setPayMethod] = useState("");
  const [placing, setPlacing] = useState(false);
  const [showNotify, setShowNotify] = useState(false);
  const [cust, setCust] = useState({ name: "", email: "", address: "" });
  const [orderDone, setOrderDone] = useState(null);

  useEffect(() => {
    const ts = Number(localStorage.getItem(NOTIFY_KEY) || 0);
    setShowNotify(!ts || Date.now() - ts > 7 * 24 * 3600 * 1000);
  }, [cartOpen]);

  const methods = (settings?.payment_methods || []).filter((m) => m.enabled);
  const selectedMethod = methods.find((m) => m.id === payMethod);
  const feePct = selectedMethod ? selectedMethod.fee_pct : null;
  const deliveryFee = settings?.delivery_fee || 0;

  const totals = useMemo(
    () => computeTotals(cart, { fulfillment, deliveryFee, feePct }),
    [cart, fulfillment, deliveryFee, feePct]
  );

  const previewDelivery = cart.length
    ? Math.max(...cart.map((c) => itemDeliveryFee(c.product, c.variant, deliveryFee)))
    : deliveryFee;

  const canCopy = cart.length > 0 && fulfillment && payMethod && !placing;
  const validEmail = /.+@.+\..+/.test(cust.email.trim());
  const canPlace = canCopy && cust.name.trim() && validEmail && cust.address.trim();

  const buildOrderText = () => {
    const lines = ["RED EYE ORDER"];
    for (const { product, qty, variant } of cart) {
      const price = unitPriceFor(product, variant);
      const ud = unitDiscount(qty, product.tiers);
      const line = price * qty - ud * qty;
      lines.push(`${product.name} #${product.code}${variant ? ` (${variant.label})` : ""} x${qty} — ${money(line)}`);
    }
    lines.push(`Bundle discount: −${money(totals.bundle_discount)}`);
    lines.push(`Fulfillment: ${fulfillment === "delivery" ? `Local delivery +${money(totals.delivery_fee)}` : "Pickup"}`);
    lines.push(`Payment: ${selectedMethod.name} (${selectedMethod.fee_pct}%) +${money(totals.payment_fee)}`);
    lines.push(`TOTAL: ${money(totals.total)}`);
    if (cust.name || cust.email || cust.address) {
      lines.push("");
      lines.push(`Name: ${cust.name}`);
      lines.push(`Email: ${cust.email}`);
      lines.push(`Shipping: ${cust.address}`);
    }
    return lines.join("\n");
  };

  const copyText = async () => {
    const text = buildOrderText();
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text; document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); } catch {}
      document.body.removeChild(ta);
    }
  };

  const copyOrder = async () => {
    if (!canCopy) return;
    await copyText();
    toast.success("Copied! Now send it to us", { description: "Paste it into Signal or Telegram." });
  };

  const placeOrder = async () => {
    if (!canPlace) return;
    setPlacing(true);
    await copyText();
    try {
      const { data } = await api.post("/orders", {
        items: cart.map((c) => ({ product_id: c.product.id, qty: c.qty, variant_label: c.variant?.label || "" })),
        fulfillment,
        payment_method_id: payMethod,
        customer_name: cust.name.trim(),
        customer_email: cust.email.trim(),
        shipping_address: cust.address.trim(),
      });
      setOrderDone(data.order_number);
      clearCart();
      toast.success("Order placed! We've been notified.", { description: "Details copied — send them on Signal or Telegram too." });
    } catch (e) {
      toast.error("Could not place order. Please try again.");
    } finally {
      setPlacing(false);
    }
  };

  const dismissNotify = () => {
    localStorage.setItem(NOTIFY_KEY, String(Date.now()));
    setShowNotify(false);
  };

  return (
    <>
      {cartOpen && <div className="fixed inset-0 bg-black/70 z-40" onClick={() => setCartOpen(false)} data-testid="cart-overlay" />}
      <aside
        className="fixed inset-y-0 right-0 w-[92vw] max-w-[440px] bg-[#0f0f0f] z-50 flex flex-col transition-transform duration-300 border-l border-[#333]"
        style={{ transform: cartOpen ? "translateX(0)" : "translateX(100%)" }}
        data-testid="cart-drawer"
      >
        <div className="h-16 flex items-center justify-between px-5 border-b border-[#E60000] shrink-0">
          <h2 className="font-heading font-black uppercase text-2xl text-[#F0EDEA]">Your Cart</h2>
          <div className="flex items-center gap-4">
            <button onClick={clearCart} className="text-[#E60000] font-heading font-bold uppercase text-sm" data-testid="clear-cart-btn">Clear</button>
            <button onClick={() => setCartOpen(false)} className="text-[#A8A2A0]" data-testid="close-cart-btn"><X size={26} /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {orderDone ? (
            <div className="text-center py-16 fade-up" data-testid="order-confirmation">
              <CheckCircle2 size={64} className="text-[#E60000] mx-auto mb-4" />
              <h3 className="font-heading font-black uppercase text-2xl text-[#F0EDEA]">Order Placed</h3>
              <p className="text-[#A8A2A0] mt-2">Order <span className="text-[#F0EDEA] font-bold">#{orderDone}</span></p>
              <p className="text-[#A8A2A0] text-sm mt-2 max-w-xs mx-auto">We've been emailed your order details. Send them to us on Signal or Telegram to confirm.</p>
              <div className="flex gap-2 mt-6">
                <a href={settings?.signal_link || "#"} target="_blank" rel="noreferrer" className="btn-skew btn-secondary flex-1 text-xs"><span><MessageCircle size={16} /> Signal</span></a>
                <a href={settings?.telegram_link || "#"} target="_blank" rel="noreferrer" className="btn-skew btn-secondary flex-1 text-xs"><span><Send size={16} /> Telegram</span></a>
              </div>
              {settings?.telegram_order_link && (
                <a href={settings.telegram_order_link} target="_blank" rel="noreferrer" className="btn-skew btn-primary w-full mt-3 text-sm" data-testid="order-telegram-link"><span><Send size={16} /> Send Order on Telegram</span></a>
              )}
              <button onClick={() => { setOrderDone(null); setFulfillment(""); setPayMethod(""); setCust({ name: "", email: "", address: "" }); setCartOpen(false); }} className="btn-skew btn-primary w-full mt-3 text-sm" data-testid="order-done-btn"><span>Done</span></button>
            </div>
          ) : (
          <>
          {cart.length === 0 && (
            <div className="text-center text-[#A8A2A0] py-16" data-testid="cart-empty">Your list is empty.</div>
          )}

          {cart.map(({ key, product, qty, variant }) => {
            const price = unitPriceFor(product, variant);
            const line = price * qty - unitDiscount(qty, product.tiers) * qty;
            const hint = nextTierHint(qty, product.tiers);
            return (
              <div key={key} className="bg-[#1C1C1C] border border-[#333] rounded-xl p-3" data-testid={`cart-item-${product.id}`}>
                <div className="flex gap-3">
                  <div className="w-20 h-20 rounded-lg overflow-hidden bg-black shrink-0">
                    {product.media?.[0] ? (
                      product.media[0].kind === "video"
                        ? <video src={mediaUrl(product.media[0])} className="w-full h-full object-cover" muted />
                        : <img src={mediaUrl(product.media[0])} className="w-full h-full object-cover" alt="" />
                    ) : <img src="/logo_wide.webp" className="w-full h-full object-contain opacity-30" alt="" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between gap-2">
                      <h4 className="font-heading font-bold uppercase text-[#F0EDEA] text-sm leading-tight">{product.name} <span className="text-[#A8A2A0] font-normal">#{product.code}</span></h4>
                      <button onClick={() => removeItem(key)} className="text-[#A8A2A0] hover:text-[#E60000] shrink-0" data-testid={`remove-item-${product.id}`}><X size={18} /></button>
                    </div>
                    {variant && <div className="text-[#FF4550] text-xs font-heading font-bold uppercase mt-0.5">{variant.label}</div>}
                    <div className="text-[#A8A2A0] text-xs mt-0.5">{money(price)} ea</div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center border border-[#333] rounded-lg overflow-hidden">
                        <button onClick={() => setQty(key, qty - 1)} className="px-2 py-1.5 text-[#F0EDEA] hover:bg-[#E60000]" data-testid={`qty-minus-${product.id}`}><Minus size={14} /></button>
                        <span className="px-3 text-[#F0EDEA] font-bold text-sm min-w-[36px] text-center" data-testid={`qty-value-${product.id}`}>{qty}</span>
                        <button onClick={() => setQty(key, qty + 1)} className="px-2 py-1.5 text-[#F0EDEA] hover:bg-[#E60000]" data-testid={`qty-plus-${product.id}`}><Plus size={14} /></button>
                      </div>
                      <span className="font-heading font-black text-[#E60000] text-lg" data-testid={`line-total-${product.id}`}>{money(line)}</span>
                    </div>
                  </div>
                </div>
                {hint && (
                  <div className="mt-2 flex items-center gap-2 text-[#FF4550] text-xs bg-[#1a0d0d] border border-[#3a1414] rounded-lg px-3 py-2" data-testid={`tier-hint-${product.id}`}>
                    <Tag size={14} /> Add {hint.add} more to save {money(hint.save)} per unit
                  </div>
                )}
              </div>
            );
          })}

          {cart.length > 0 && (
            <>
              <div>
                <div className="eyebrow text-xs mb-2">Fulfillment</div>
                <div className="grid grid-cols-2 gap-3">
                  <button onClick={() => setFulfillment("pickup")} data-testid="fulfillment-pickup" className={`rounded-xl border p-4 text-center transition-colors ${fulfillment === "pickup" ? "border-[#E60000] bg-[#1a0d0d]" : "border-[#333] bg-[#1C1C1C]"}`}>
                    <Store size={22} className="mx-auto text-[#F0EDEA]" />
                    <div className="font-heading font-bold uppercase text-sm text-[#F0EDEA] mt-2">Pickup</div>
                    <div className="text-[#A8A2A0] text-xs">No fee</div>
                  </button>
                  <button onClick={() => setFulfillment("delivery")} data-testid="fulfillment-delivery" className={`rounded-xl border p-4 text-center transition-colors ${fulfillment === "delivery" ? "border-[#E60000] bg-[#1a0d0d]" : "border-[#333] bg-[#1C1C1C]"}`}>
                    <Bike size={22} className="mx-auto text-[#F0EDEA]" />
                    <div className="font-heading font-bold uppercase text-sm text-[#F0EDEA] mt-2">Local delivery</div>
                    <div className="text-[#A8A2A0] text-xs">+{money(previewDelivery)}/order</div>
                  </button>
                </div>
              </div>

              <div>
                <div className="eyebrow text-xs mb-2">Payment Method</div>
                <Select value={payMethod} onValueChange={setPayMethod}>
                  <SelectTrigger className="w-full bg-[#1C1C1C] border-[#333] text-[#F0EDEA] h-12 rounded-xl" data-testid="payment-method-select">
                    <div className="flex items-center gap-2"><CreditCard size={16} className="text-[#A8A2A0]" /><SelectValue placeholder="Choose payment method" /></div>
                  </SelectTrigger>
                  <SelectContent className="bg-[#1C1C1C] border-[#333] text-[#F0EDEA]">
                    {methods.map((m) => (
                      <SelectItem key={m.id} value={m.id} data-testid={`payment-option-${m.id}`} className="focus:bg-[#E60000] focus:text-white">
                        {m.name} ({m.fee_pct}% fee)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <div className="eyebrow text-xs mb-2">Your Details</div>
                <div className="space-y-2">
                  <input value={cust.name} onChange={(e) => setCust({ ...cust, name: e.target.value })} placeholder="Full name" className="w-full bg-[#1C1C1C] border border-[#333] rounded-xl h-11 px-3 text-[#F0EDEA] placeholder-[#A8A2A0] focus:border-[#E60000] outline-none" data-testid="checkout-name" />
                  <input type="email" value={cust.email} onChange={(e) => setCust({ ...cust, email: e.target.value })} placeholder="Email address" className="w-full bg-[#1C1C1C] border border-[#333] rounded-xl h-11 px-3 text-[#F0EDEA] placeholder-[#A8A2A0] focus:border-[#E60000] outline-none" data-testid="checkout-email" />
                  <textarea value={cust.address} onChange={(e) => setCust({ ...cust, address: e.target.value })} placeholder="Shipping address" className="w-full bg-[#1C1C1C] border border-[#333] rounded-xl h-20 p-3 text-[#F0EDEA] placeholder-[#A8A2A0] focus:border-[#E60000] outline-none" data-testid="checkout-address" />
                </div>
              </div>

              <div className="bg-[#1C1C1C] border border-[#333] rounded-xl p-4 space-y-2" data-testid="cart-totals">
                <Row label="Subtotal" value={money(totals.subtotal)} testid="total-subtotal" />
                <Row label="Bundle discount" value={`−${money(totals.bundle_discount)}`} green testid="total-bundle" />
                {fulfillment === "delivery" && <Row label="Delivery fee" value={`+${money(totals.delivery_fee)}`} testid="total-delivery" />}
                {selectedMethod && <Row label={`${selectedMethod.name} fee (${selectedMethod.fee_pct}%)`} value={`+${money(totals.payment_fee)}`} testid="total-payfee" />}
                <div className="border-t border-[#333] pt-2 flex justify-between items-baseline">
                  <span className="font-heading font-black uppercase text-[#A8A2A0]">Total</span>
                  <span className="font-heading font-black text-3xl text-[#E60000]" data-testid="total-grand">{money(totals.total)}</span>
                </div>
              </div>

              <button onClick={placeOrder} disabled={!canPlace} className="btn-skew btn-primary w-full" data-testid="place-order-btn">
                <span><Check size={16} /> {placing ? "Placing..." : "Place Order →"}</span>
              </button>
              <button onClick={copyOrder} disabled={!canCopy} className="btn-skew btn-secondary w-full text-sm" data-testid="copy-order-btn">
                <span><Copy size={16} /> Copy Order Details</span>
              </button>
              <p className="text-[#A8A2A0] text-[11px] text-center">Placing an order emails us your details. You can also copy them and send on Signal or Telegram.</p>

              <div className="flex gap-2">
                <a href={settings?.signal_link || "#"} target="_blank" rel="noreferrer" className="btn-skew btn-secondary flex-1 text-xs" data-testid="cart-signal"><span><MessageCircle size={16} /> Signal</span></a>
                <a href={settings?.telegram_link || "#"} target="_blank" rel="noreferrer" className="btn-skew btn-secondary flex-1 text-xs" data-testid="cart-telegram"><span><Send size={16} /> Telegram</span></a>
              </div>
            </>
          )}
          </>
          )}
        </div>

        {showNotify && (
          <div className="shrink-0 border-t border-[#333] bg-[#111] p-3 flex items-center gap-3" data-testid="notify-bar">
            <Bell size={20} className="text-[#E60000] shrink-0" />
            <span className="text-xs text-[#F0EDEA] flex-1">Get notified when new products drop</span>
            <a href={settings?.telegram_channel_link || "#"} target="_blank" rel="noreferrer" className="btn-skew btn-primary text-[11px] px-3 py-2" data-testid="notify-join-btn"><span>Join</span></a>
            <button onClick={dismissNotify} className="text-[#A8A2A0]" data-testid="notify-dismiss-btn"><X size={18} /></button>
          </div>
        )}
      </aside>
    </>
  );
}

function Row({ label, value, green, testid }) {
  return (
    <div className="flex justify-between text-sm" data-testid={testid}>
      <span className="text-[#A8A2A0]">{label}</span>
      <span className={green ? "text-green-400 font-semibold" : "text-[#F0EDEA] font-semibold"}>{value}</span>
    </div>
  );
}
